/**
 * App shell.
 *
 * Two layouts in one component:
 *   - md+: left sidebar with nav + identity, content takes the rest.
 *   - sm and down: top bar with wordmark + bell + sign-out, content
 *     stacks below.
 *
 * The notification drawer is mounted once in this layout. The sidebar
 * (desktop) and the header (mobile) both ask Layout to open it.
 * Unread count is fetched on mount and refreshed when the drawer
 * closes and when the route changes.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { me, gmail } from '@/lib/api';
import { useAppTheme } from '@/lib/app-theme';
import { useToast } from './Toast';
import { Sidebar } from './Sidebar';
import { NotificationDrawer } from './NotificationDrawer';
import { MobileNavDrawer } from './MobileNavDrawer';
import { ThemeToggle } from './ThemeToggle';
import { EmailSyncButton } from './EmailSyncButton';
import { Monomark } from './Logo';
import { IconBell, IconMenu } from './Icons';

/** localStorage key for the last successful email sync (ms epoch). */
const LAST_EMAIL_SYNC_KEY = 'kg:lastEmailSync';

export function Layout() {
  const { user } = useAuth();
  const location = useLocation();
  const toast = useToast();
  const [unread, setUnread] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const { theme, toggle: toggleTheme } = useAppTheme();

  // Belt-and-braces: the drawer closes itself on nav taps, but a
  // programmatic route change (deep link, redirect) should never
  // leave it covering the new page.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Email sync state. Email is pulled server-side every ~5 min, but the
  // app doesn't refetch on its own when the PWA is reopened, so we let
  // the user force a pull + view refresh and show how fresh it is.
  const [syncing, setSyncing] = useState(false);
  const [lastEmailSyncAt] = useState<number | null>(() => {
    const v = Number(localStorage.getItem(LAST_EMAIL_SYNC_KEY) || 0);
    return v > 0 ? v : null;
  });
  // Ticks once a minute so the "Synced Xm ago" label stays honest.
  const [now, setNow] = useState(() => Date.now());
  const syncingRef = useRef(false);

  const refreshUnread = useCallback(async () => {
    try {
      const list = await me.notifications(true);
      setUnread(list.length);
    } catch {
      // Silent: drawer can still be opened manually if this fails.
    }
  }, []);

  useEffect(() => {
    void refreshUnread();
  }, [refreshUnread, location.pathname]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Pull the latest email now, then hard-refresh the view so every page
  // (inbox, dashboard) reflects it. The freshness label reads the
  // persisted timestamp back after the reload.
  const syncEmail = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setSyncing(true);
    try {
      await gmail.sync();
      try {
        localStorage.setItem(LAST_EMAIL_SYNC_KEY, String(Date.now()));
      } catch {
        // Private-mode / quota: skip persistence, still reload.
      }
      window.location.reload();
    } catch (err) {
      syncingRef.current = false;
      setSyncing(false);
      toast.error(err instanceof Error ? err.message : 'Could not sync email');
    }
  }, [toast]);

  // Auto-sync when the installed PWA is brought back to the foreground
  // (so reopening from the home screen always lands on current mail).
  // Gated to standalone display-mode so desktop tab-switching never
  // triggers a reload, and throttled to avoid reloading on a quick
  // glance away.
  useEffect(() => {
    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;
    if (!standalone) return;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const last = Number(localStorage.getItem(LAST_EMAIL_SYNC_KEY) || 0);
      if (Date.now() - last > 120_000) void syncEmail();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [syncEmail]);

  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => {
    setDrawerOpen(false);
    void refreshUnread();
  };

  return (
    <div className="min-h-screen flex bg-kg-grey-50 text-kg-black dark:bg-kg-black dark:text-kg-grey-50">
      <Sidebar
        unreadCount={unread}
        onOpenNotifications={openDrawer}
        themeToggle={
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
        }
        syncControl={
          <EmailSyncButton
            variant="sidebar"
            syncing={syncing}
            lastSyncedAt={lastEmailSyncAt}
            now={now}
            onSync={() => void syncEmail()}
          />
        }
      />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile-only top bar. Hidden on md+ since the sidebar covers nav. */}
        <header className="md:hidden kg-glass border-b border-kg-grey-100/80 sticky top-0 z-30 dark:border-white/10 pt-[env(safe-area-inset-top)]">
          <div className="px-4 h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => setNavOpen(true)}
                aria-label="Open navigation"
                aria-expanded={navOpen}
                className="p-2 -ml-2 text-kg-grey-700 hover:text-kg-black dark:text-kg-grey-300 dark:hover:text-kg-grey-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kg-black"
              >
                <IconMenu className="w-5 h-5" />
              </button>
              <Link
                to="/"
                className="flex items-center"
                aria-label="Kilogram dashboard"
              >
                <Monomark variant="black" className="h-7 w-auto dark:hidden" />
                <Monomark variant="white" className="h-7 w-auto hidden dark:inline-block" />
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <EmailSyncButton
                variant="bar"
                syncing={syncing}
                lastSyncedAt={lastEmailSyncAt}
                now={now}
                onSync={() => void syncEmail()}
              />
              <ThemeToggle theme={theme} onToggle={toggleTheme} />
              <button
                onClick={openDrawer}
                aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}
                className="relative p-2 text-kg-grey-700 hover:text-kg-black dark:text-kg-grey-300 dark:hover:text-kg-grey-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kg-black"
              >
                <IconBell className="w-5 h-5" />
                {unread > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute top-1 right-1 w-2 h-2 bg-red-700 rounded-full"
                  />
                )}
              </button>
              {/* Sign out moved into the nav drawer next to the
                  identity block — the bare arrow glyph here was
                  cryptic and one tap from accidental. */}
            </div>
          </div>
        </header>

        <main className="flex-1">
          {/* Container width bumped from max-w-6xl (1152px) to
              max-w-screen-2xl (1536px) so the dense tables (dashboard
              rows, preprod schedule, contractor list) have room to
              breathe. Px padding stays as-is so gutters on big monitors
              still feel comfortable. */}
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
            <Outlet />
          </div>
        </main>
      </div>

      <MobileNavDrawer open={navOpen} onClose={() => setNavOpen(false)} />

      <NotificationDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onUnreadChange={() => void refreshUnread()}
      />
      {/* user reference: keeps lint happy if we add more uses later */}
      {user ? null : null}
    </div>
  );
}
